import type { CSSProperties, FC, ReactNode } from 'react';
import { Typography } from 'antd';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';

import { ActivityIconAvatar } from '../ResourceActivityIconAvatar';

export const ACTIVITY_TIMELINE_MARKER_SIZE = 24;
export const ACTIVITY_TIMELINE_MARKER_COLUMN_WIDTH = 40;

export interface ActivityTimelineStep {
    content: ReactNode;
    key: string;
    style?: CSSProperties;
}

interface ActivityTimelineItemProps {
    action?: ReactNode;
    density?: 'compact' | 'regular';
    icon: ReactNode;
    isLast?: boolean;
    metadata?: ReactNode;
    nestingDepth?: 0 | 1;
    nextNestingDepth?: 0 | 1;
    steps?: ActivityTimelineStep[];
    title: ReactNode;
}

/**
 * Shared activity-row geometry used by resource activity, audit logs, and other
 * event streams. Callers own the event language and optional custom steps;
 * this primitive owns marker, connector, spacing, and overflow behavior.
 */
const ActivityTimelineItem: FC<ActivityTimelineItemProps> = ({
    action,
    density = 'regular',
    icon,
    isLast = false,
    metadata,
    nestingDepth = 0,
    nextNestingDepth = 0,
    steps = [],
    title,
}) => {
    const { token } = useAntDesignTheme();
    const markerColumnCount = nestingDepth + 1;
    const markerCenter =
        ACTIVITY_TIMELINE_MARKER_COLUMN_WIDTH * nestingDepth + ACTIVITY_TIMELINE_MARKER_COLUMN_WIDTH / 2;
    const continuesAtCurrentDepth = !isLast && nextNestingDepth >= nestingDepth;

    return (
        <div
            data-activity-timeline-item='true'
            data-nesting-depth={nestingDepth}
            style={{
                alignItems: 'start',
                boxSizing: 'border-box',
                display: 'grid',
                gridTemplateColumns: `repeat(${markerColumnCount}, ${ACTIVITY_TIMELINE_MARKER_COLUMN_WIDTH}px) minmax(0, 1fr)`,
                minWidth: 0,
                paddingBottom: isLast ? 0 : density === 'compact' ? '0.375rem' : '0.875rem',
                position: 'relative',
                width: '100%',
            }}
        >
            {nestingDepth === 1 && (
                <>
                    <div
                        aria-hidden='true'
                        data-activity-timeline-parent-connector='true'
                        style={{
                            background: token.colorBorder,
                            bottom: isLast
                                ? `calc(100% - ${ACTIVITY_TIMELINE_MARKER_SIZE / 2}px)`
                                : -(ACTIVITY_TIMELINE_MARKER_SIZE / 2),
                            left: ACTIVITY_TIMELINE_MARKER_COLUMN_WIDTH / 2,
                            position: 'absolute',
                            top: -(ACTIVITY_TIMELINE_MARKER_SIZE / 2),
                            transform: 'translateX(-0.5px)',
                            width: 1,
                        }}
                    />
                    <div
                        aria-hidden='true'
                        data-activity-timeline-branch='true'
                        style={{
                            background: token.colorBorder,
                            height: 1,
                            left: ACTIVITY_TIMELINE_MARKER_COLUMN_WIDTH / 2,
                            position: 'absolute',
                            top: ACTIVITY_TIMELINE_MARKER_SIZE / 2,
                            width: ACTIVITY_TIMELINE_MARKER_COLUMN_WIDTH,
                        }}
                    />
                </>
            )}
            {continuesAtCurrentDepth && (
                <div
                    aria-hidden='true'
                    data-activity-timeline-connector='true'
                    style={{
                        background: token.colorBorder,
                        bottom: -(ACTIVITY_TIMELINE_MARKER_SIZE / 2),
                        left: markerCenter,
                        position: 'absolute',
                        top: ACTIVITY_TIMELINE_MARKER_SIZE / 2,
                        transform: 'translateX(-0.5px)',
                        width: 1,
                    }}
                />
            )}
            <div
                style={{
                    display: 'flex',
                    gridColumn: markerColumnCount,
                    justifyContent: 'center',
                    position: 'relative',
                    zIndex: 1,
                }}
            >
                <ActivityIconAvatar icon={icon} size={ACTIVITY_TIMELINE_MARKER_SIZE} />
            </div>
            <div
                data-activity-timeline-content='true'
                style={{
                    boxSizing: 'border-box',
                    gridColumn: markerColumnCount + 1,
                    minWidth: 0,
                    paddingLeft: '0.75rem',
                    width: '100%',
                }}
            >
                <div style={{ alignItems: 'center', display: 'flex', gap: '0.5rem', minWidth: 0, width: '100%' }}>
                    <div style={{ flex: '1 1 0%', minWidth: 0 }}>
                        <Typography.Text style={{ display: 'block', minWidth: 0 }}>{title}</Typography.Text>
                    </div>
                    {action && (
                        <div data-activity-timeline-action='true' style={{ flex: '0 0 auto', marginLeft: 'auto' }}>
                            {action}
                        </div>
                    )}
                </div>
                {metadata && <div style={{ marginTop: '0.125rem', width: '100%' }}>{metadata}</div>}
                {steps.map((step) => (
                    <div key={step.key} style={{ marginTop: '0.75rem', ...step.style }}>
                        {step.content}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default ActivityTimelineItem;
